/**
 * Type definitions for the Clients management area.
 *
 * These mirror the API service contracts for the `clients`, `client-modules`,
 * and `client-tools` Feathers services (see the API migrations of the same
 * name). They are intentionally local to the views module — the Clients
 * ViewStores read the registered ServiceStores by hand (find/create/patch/
 * remove + entity cache) rather than via generated `*.types`, so these local
 * record/form types are the single source of shape for the area.
 */

export type ClientStatus = "active" | "inactive" | "prospect" | "archived" | string;

/** A managed client (tenant) — backed by the existing `clients` table. */
export interface ClientRecord {
  id: string;
  name: string;
  code: string;
  type?: string;
  parentClientId?: string | null;
  isMultiCompany?: boolean;
  /**
   * The Client-Coordinator registry's slug for this client (the C8 crosswalk):
   * DECLARED, never derived — HQ `PPF` is registry `premier`. null = no repo.
   */
  coordinatorSlug?: string | null;
  /**
   * [hub] H1 — who owns this client's host work (users.id). Questions that
   * block that work go to them, and so does the work. Admin-set.
   */
  developerUserId?: number | null;
  /** Resolved by the API from `developerUserId`; never stored. */
  developerEmail?: string | null;
  status?: ClientStatus;
  industry?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  taxId?: string | null;
  fiscalYearEnd?: string | null;
  defaultCurrency?: string;
  notes?: string | null;
  logoUrl?: string | null;
  /** Onboarding lifecycle (columns exist on the clients table + patch schema). */
  onboardingCompleted?: boolean;
  onboardingCompletedAt?: string | null;
  onboardingStep?: string | null;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: number | null;
  updatedBy?: number | null;
  /** Soft-delete column (added by a later API migration). */
  deletedAt?: string | null;
}

/** Editable subset of a client used by the create/edit form. */
export type ClientFormData = Partial<
  Pick<
    ClientRecord,
    | "name"
    | "code"
    | "type"
    | "parentClientId"
    | "isMultiCompany"
    | "coordinatorSlug"
    | "developerUserId"
    | "status"
    | "industry"
    | "address"
    | "city"
    | "state"
    | "zip"
    | "phone"
    | "email"
    | "website"
    | "taxId"
    | "fiscalYearEnd"
    | "defaultCurrency"
    | "notes"
  >
>;

export type ModuleStatus = "enabled" | "disabled" | string;

/** A module enabled for a client — backed by the `client_modules` table. */
export interface ClientModuleRecord {
  id: string;
  clientId: string;
  moduleKey: string;
  displayName: string;
  status: ModuleStatus;
  isDeployable: boolean;
  metadata?: string | null;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: number | null;
  updatedBy?: number | null;
  deletedAt?: string | null;
}

export type ClientModuleFormData = Partial<
  Pick<
    ClientModuleRecord,
    "moduleKey" | "displayName" | "status" | "isDeployable" | "metadata"
  >
>;

/**
 * An entry in the org-wide module vocabulary — backed by the `module_catalog`
 * table. Feeds the Modules panel's assignment picker; `client_modules` rows
 * stay free text, so uncatalogued keys remain valid.
 */
export interface ModuleCatalogRecord {
  id: string;
  moduleKey: string;
  displayName: string;
  description?: string | null;
  isDeployable: boolean;
  sortOrder?: number | null;
  /**
   * May a CLIENT be assigned this entry? Sync-owned, mirrored from the
   * Coordinator catalog. `false` for the listed-not-resolvable entries
   * (`core`, `libraries`, `payroll`) — this picker must exclude them.
   * Optional because a pre-migration API omits it entirely.
   */
  assignable?: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: number | null;
  updatedBy?: number | null;
  deletedAt?: string | null;
}

/**
 * NOTE: this is the CLIENT-PICKER view of a catalog row — deliberately partial.
 * The full governed record (kind, governedRepo, category, domainRole, status,
 * aliases, componentOf, packages, configureSymbol, …) lives in
 * `views/modules/modules.types.ts`, which mirrors the whole TypeBox schema.
 */

export type ToolType = "internal" | "external" | string;
export type ToolStatus = "active" | "inactive" | string;

/** A tool/link configured for a client — backed by the `client_tools` table. */
export interface ClientToolRecord {
  id: string;
  clientId: string;
  toolKey: string;
  toolType: ToolType;
  name: string;
  url?: string | null;
  status: ToolStatus;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: number | null;
  updatedBy?: number | null;
  deletedAt?: string | null;
}

export type ClientToolFormData = Partial<
  Pick<ClientToolRecord, "toolKey" | "toolType" | "name" | "url" | "status">
>;

/**
 * One row of the unified Modules & Tools ("Deliverables") table on Client 360.
 * Discriminates a client module vs a client tool so the table can render one
 * list with a Type column and a per-kind details drawer.
 */
export type ClientDeliverable =
  | { kind: "module"; key: string; record: ClientModuleRecord }
  | { kind: "tool"; key: string; record: ClientToolRecord };

// ---------------------------------------------------------------------------
// Client 360 — contacts
// ---------------------------------------------------------------------------

/** A person at the client — backed by the `client_contacts` table. */
export interface ClientContactRecord {
  id: string;
  clientId: string;
  name: string;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  isPrimary?: boolean;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: number | null;
  updatedBy?: number | null;
  deletedAt?: string | null;
}

export type ClientContactFormData = Partial<
  Pick<ClientContactRecord, "name" | "title" | "email" | "phone" | "isPrimary" | "notes">
>;

// ---------------------------------------------------------------------------
// Client 360 — onboarding lifecycle
// ---------------------------------------------------------------------------

/**
 * Ordered onboarding steps rendered by the Overview tab's stepper. The step
 * KEY is what is persisted to `clients.onboardingStep` (NVARCHAR(50)); the
 * label is display-only. Keep keys <= 50 chars to satisfy the patch schema.
 */
export const CLIENT_ONBOARDING_STEPS = [
  { key: "contract-signed", label: "Contract Signed" },
  { key: "environment-provisioned", label: "Environment Provisioned" },
  { key: "modules-configured", label: "Modules Configured" },
  { key: "devops-linked", label: "DevOps Linked" },
  { key: "instances-deployed", label: "Instances Deployed" },
  { key: "training", label: "Training" },
  { key: "live", label: "Live" },
] as const;

export type ClientOnboardingStepKey = (typeof CLIENT_ONBOARDING_STEPS)[number]["key"];

// ---------------------------------------------------------------------------
// Client 360 — rollups (`client-summary`) and health (`client-health`)
// ---------------------------------------------------------------------------

/**
 * Per-client rollup counts from the find-only `client-summary` service.
 * Field names are tolerant (the service is landing in the same phase); the
 * store normalises whichever of these the API serialises.
 */
export interface ClientSummaryRecord {
  clientId: string;
  instanceCount?: number;
  runningInstanceCount?: number;
  // Alternate nested serialisation tolerated by the normaliser.
  instances?: { total?: number; running?: number } | number;
}

export type ClientHealthStatus = "green" | "yellow" | "red" | string;

/**
 * Computed health from the find-only `client-health` service:
 * green/yellow/red plus human-readable reasons. `signals`/`score` are
 * tolerated alternates from the survey-era shape.
 */
export interface ClientHealthRecord {
  clientId: string;
  /** Landed API field (client-health.schema.ts): green/yellow/red. */
  health?: ClientHealthStatus;
  status?: ClientHealthStatus;
  reasons?: string[];
  signals?: string[];
  score?: ClientHealthStatus;
}

/**
 * One row of the append-only `client-health-history` service
 * (client_health_history). Written internally on each client-health read, so
 * the series is a record of OBSERVED verdicts, not a fixed-interval sample.
 *
 * NOTE the shape: the API stores a TIER, not a number — there is no `score`
 * column. `reasons` is a JSON-encoded string array ('[]' when green). The
 * overview sparkline therefore plots {@link healthTierScore}, a tier→0-100
 * projection, rather than a stored metric.
 */
export interface ClientHealthHistoryRecord {
  id: number;
  clientId: string;
  /** 'green' | 'yellow' | 'red' — the computed tier at capture time. */
  health: ClientHealthStatus;
  /** JSON-encoded array of reason strings. */
  reasons?: string;
  createdAt: string;
}

/** Tier → 0-100 projection so the health series can be drawn as a line. */
export const healthTierScore = (health?: string | null): number => {
  switch (health) {
    case "green":
      return 100;
    case "yellow":
      return 55;
    case "red":
      return 10;
    default:
      return 0;
  }
};

// ---------------------------------------------------------------------------
// Client 360 — billing (invoices for THIS client; read-only)
// ---------------------------------------------------------------------------

/**
 * An `invoices` row as the Billing tab reads it. Deliberately a LOCAL, minimal
 * mirror of the billing module's row type: the Client 360 tab only reads, and
 * duplicating five fields is cheaper than coupling the Clients area to a
 * sibling feature module's ViewStore.
 *
 * Money columns are MSSQL decimals, which tedious serialises as strings — hence
 * `number | string` and the {@link invoiceAmount} coercion.
 */
export interface ClientInvoiceRecord {
  id: string;
  invoiceNumber: string;
  clientId: string;
  issueDate?: string | null;
  dueDate?: string | null;
  status: string;
  total?: number | string | null;
}

/** Coerce a decimal column (string from tedious) to a number. */
export const invoiceAmount = (value: unknown): number => {
  if (value === null || value === undefined || value === "") return 0;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** `$1,234.50` — the Billing tab's only money formatter. */
export const formatInvoiceAmount = (value: unknown): string =>
  `$${invoiceAmount(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/** MUI chip colour per invoice status. */
export const invoiceStatusColor = (
  status?: string | null
): "default" | "info" | "success" | "error" | "warning" => {
  switch (status) {
    case "paid":
      return "success";
    case "overdue":
      return "error";
    case "sent":
      return "info";
    case "void":
      return "warning";
    default:
      return "default";
  }
};

// ---------------------------------------------------------------------------
// Client 360 — meetings (read-ai-meetings scoped to this client)
// ---------------------------------------------------------------------------

/**
 * A `read-ai-meetings` row as the Meetings tab reads it — a local read-only
 * subset of the Meeting Intelligence row type (same reason as
 * {@link ClientInvoiceRecord}: the tab reads, it does not own the entity).
 */
export interface ClientMeetingRecord {
  id: number;
  title: string;
  startTime?: string | null;
  clientId?: string | null;
  mappingStatus?: string | null;
  processed?: boolean;
  /** Every action item this meeting produced, closed ones INCLUDED. */
  actionItemCount?: number;
  /** How many of those are still open — i.e. still work. Absent on an API that
   *  predates the close path; fall back to actionItemCount, never to 0. */
  openActionItemCount?: number;
  createdAt?: string;
}

/** Human label + chip colour for a meeting's client-mapping state. */
export const MEETING_MAPPING_META: Record<
  string,
  { label: string; color: "success" | "warning" | "info" | "default" }
> = {
  auto: { label: "Auto-mapped", color: "success" },
  manual: { label: "Mapped", color: "success" },
  needs_review: { label: "Needs review", color: "warning" },
  internal: { label: "Internal", color: "default" },
};

// ---------------------------------------------------------------------------
// Client 360 — activity timeline (communications + audit-log + devops-events)
// ---------------------------------------------------------------------------

/** A `communications` row (project-management domain; clientId scoped). */
export interface CommunicationRecord {
  id: number | string;
  clientId: string;
  projectId?: number | null;
  sourceType: string;
  sourceId?: string | null;
  subject?: string | null;
  summary?: string | null;
  participants?: string | null;
  occurredAt?: string | null;
  createdAt?: string;
  deletedAt?: string | null;
}

/** An `audit-log` row scoped to this client. */
export interface AuditLogRecord {
  id: number | string;
  clientId?: string | null;
  entityType: string;
  entityId: string;
  action: "create" | "update" | "delete" | string;
  userId?: number | null;
  changedFields?: string | null;
  createdAt?: string;
}

/** A persisted `devops-events` row (find-only feed; clientId scoped). */
export interface DevOpsEventRecord {
  id: number | string;
  connectionId?: string | null;
  clientId?: string | null;
  /** The Azure project the event belongs to (columns since 2026-09-03 —
   *  Migration A); absent on rows from an older API, null when the webhook
   *  payload named neither. Project 360 filters its feed on these. */
  devopsProjectId?: string | null;
  devopsProjectName?: string | null;
  type: string;
  /** Human-readable headline derived by the API (devops-events.schema.ts). */
  title?: string | null;
  /** JSON payload — serialised as a string or an object depending on the API. */
  summary?: string | Record<string, unknown> | null;
  /** When the event happened (the feed's sort field). */
  occurredAt?: string;
  createdAt?: string;
}

export type ClientActivityType = "communication" | "audit" | "devops";

/** One merged, render-ready timeline entry on the Activity tab. */
export interface ClientActivityItem {
  /** Stable list key: `<type>-<sourceId>`. */
  key: string;
  type: ClientActivityType;
  /** ISO timestamp the timeline sorts on (newest first). */
  at: string;
  title: string;
  detail?: string;
}

// ---------------------------------------------------------------------------
// Client 360 — documents (widened `documents` service, entityType 'client')
// ---------------------------------------------------------------------------

/** A `documents` row attached to this client (entityType='client'). */
export interface ClientDocumentRecord {
  id: string;
  fileName: string;
  originalFileName?: string;
  fileSize?: number;
  mimeType?: string;
  storageUrl?: string;
  thumbnailUrl?: string;
  category?: string;
  entityType?: string;
  entityId?: string;
  status?: string;
  uploadedBy?: number;
  uploadedAt?: string;
  createdAt?: string;
  deletedAt?: string | null;
}

// ---------------------------------------------------------------------------
// Client 360 — live status for DevOps links
// ---------------------------------------------------------------------------

/** Live status for a pipeline-type link (one definition's latest build). */
export interface DevOpsLinkPipelineStatus {
  kind: "pipeline";
  /** Flat build status string (see devops.types DevOpsBuildStatus). */
  status: string;
  webUrl?: string;
}

/** Aggregated status for a project-type link (all definitions in the project). */
export interface DevOpsLinkProjectStatus {
  kind: "project";
  total: number;
  succeeded: number;
  failed: number;
  inProgress: number;
}

export type DevOpsLinkLiveStatus = DevOpsLinkPipelineStatus | DevOpsLinkProjectStatus;

// ---------------------------------------------------------------------------
// Client 360 — Meeting Intelligence (client-sentiment + relationship ledger)
// ---------------------------------------------------------------------------

/**
 * Per-client happiness rollup from the `client-sentiment` service — the recent
 * `meeting.happiness-score` analyses averaged into one signal. `score` is 0–100
 * (null when no scored meetings exist); `tier` drives the chip color.
 */
export interface ClientSentiment {
  clientId: string;
  score: number | null;
  label: "happy" | "neutral" | "at_risk" | "unknown";
  tier: "green" | "yellow" | "red" | "unknown";
  sampleSize: number;
}

/**
 * One relationship-ledger row from the `relationship-items` service — an
 * expectation / need / commitment extracted from meetings (or hand-added).
 * For Client 360 we query `subjectType='client'` + `subjectId=<clientId>`:
 * what this client expects of us, needs from a partner, or has committed to.
 */
export interface RelationshipItem {
  id: number;
  /** "expectation" | "need" | "commitment". */
  kind: string;
  subjectType: string;
  subjectId?: string | null;
  /** Other party the item is directed at: "us" | "client" | "partner" | null. */
  counterpartyType?: string | null;
  counterpartyId?: string | null;
  text: string;
  /** "open" by default; "resolved"/"closed" once handled. */
  status: string;
  priority?: string | null;
  sourceMeetingId?: number | null;
  dueDate?: string | null;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

/**
 * One uploaded Outlook email thread (`email-messages` service) attributed to a
 * client. The original .eml/.msg + attachments are stored as documents under the
 * linked `meetingId`; `sourceDocumentId` is the original email file.
 */
export interface EmailMessageRow {
  id: number;
  meetingId: number;
  clientId?: string | null;
  subject?: string | null;
  fromName?: string | null;
  fromEmail?: string | null;
  /** JSON-encoded arrays of { name, email }. */
  toJson?: string | null;
  ccJson?: string | null;
  sentAt?: string | null;
  format: string;
  sourceDocumentId?: string | null;
  attachmentCount: number;
  clientMatchSource?: string | null;
  createdAt?: string;
}
